'use client';

import classNames from 'classnames';
import {LocalizedHtml, useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import {BlockList} from '@udemy-v2/react-core-components';
import {publishEvent} from '@udemy-v2/event-tracking';
import {
    aggregateCollectionStats,
    FALLBACK_COLLECTION_STATS,
    PlanPeriod,
    PlanPeriodStyle,
    SubscribeFromPageCookie,
    SubscriptionCTA,
    SubscriptionLearnMoreEvent,
} from '@udemy-v2/react-subscription-browse';
import {CollapsiblePurchaseSection} from '@/features/course-landing-page/components/collapsible-purchase-section/collapsible-purchase-section.react-component';
import {
    CLPPurchaseOption,
    useClpPurchaseContext,
} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useClpNoticeContext} from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import styles from './non-catalog-pp-offer.module.scss';

const UI_REGION = 'purchase_section_tab_non_catalog';
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];

export const CollapsibleNonCatalogPPContent = () => {
    const {gettext, interpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const course = useClpCourse();
    const {
        nonCatalogPersonalPlan,
        nonCatalogSubscriptionCTAConfiguration,
        selectedPurchaseOption,
        setSelectedPurchaseOption,
    } = useClpPurchaseContext();
    const clpNoticeContext = useClpNoticeContext();
    const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;

    const isExpanded = selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;

    if (!nonCatalogPersonalPlan) {
        return null;
    }

    const contentCollections = nonCatalogPersonalPlan.contentCollections;
    const subscriptionPlanStats = contentCollections
        ? aggregateCollectionStats(
              contentCollections.map(
                  (contentCollection: (typeof contentCollections)[number]) =>
                      contentCollection.stats,
              ),
          )
        : FALLBACK_COLLECTION_STATS;

    const formattedMonthlyListPrice = nonCatalogPersonalPlan.formattedMonthlyAmount;

    const learnMoreText = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext('Learn more');
    const subscriptionContextLearnMoreUrl =
        nonCatalogPersonalPlan.urlLearnMore ?? 'https://udemy.com/personal-plan/';
    const learnMoreUrl =
        subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
    const learnMoreTarget =
        subscriptionPriceBoxNotice?.subtitle_link_target === 'new_tab' ? '_blank' : '_self';

    const ctaUrl = (() => {
        const params = new URLSearchParams({
            'non-catalog-course': course.id,
        });
        return `/subscription-checkout/express/?${params.toString()}`;
    })();

    const handleLearnMoreClick = () => {
        // When navigating to Personal Plan info / plan selection pages from CLP, set the
        // subscribe_from_page cookie so subscription checkout can attribute to this course.
        if (SUBSCRIPTION_ATTRIBUTION_PATHS.some((path) => learnMoreUrl.includes(path))) {
            SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
        }

        if (nonCatalogPersonalPlan) {
            publishEvent(
                new SubscriptionLearnMoreEvent({
                    planId: nonCatalogPersonalPlan.id,
                    planType: nonCatalogPersonalPlan.productType,
                    uiRegion: UI_REGION,
                    planPriceIds: nonCatalogPersonalPlan.planPriceOptionIds,
                }),
            );
        }
    };

    const handleCtaClick = () => {
        SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
    };

    const title = (
        <>
            <span className={classNames('ud-text-md')}>{gettext('Subscribe and save')}</span>
            <PlanPeriod
                listPriceText={formattedMonthlyListPrice}
                // The non-catalog PP offer targets paid subscription starts only, so we don't display any trial information. We also don't display discounts in this case as it's not applicable for this promo.
                trialPeriodDays={0}
                style={PlanPeriodStyle.TITLE}
            />
        </>
    );

    return (
        <CollapsiblePurchaseSection
            expanded={isExpanded}
            title={title}
            onExpand={() => setSelectedPurchaseOption(CLPPurchaseOption.SUBSCRIPTION)}
        >
            <div className={styles['value-prop-badge']}>
                <Icon
                    className={styles['value-prop-icon']}
                    glyph="verified"
                    color="positive"
                    size="small"
                    label=""
                />
                <span className={styles['value-prop-text']}>
                    <LocalizedHtml
                        html={gettext(
                            '<span class="bold">Get this course for free</span> when you subscribe. Terms apply.',
                        )}
                        interpolate={{
                            bold: <span className="ud-text-bold" />,
                        }}
                    />
                </span>
            </div>
            <BlockList padding="tight" size="small">
                <BlockList.Item icon={<Icon label="" size="xsmall" glyph="video-playlist" />}>
                    <span className="ud-text-sm">
                        {interpolate(
                            gettext('Access to %(courses_count)s+ top-rated courses'),
                            {courses_count: formatNumber(subscriptionPlanStats.numberOfCourses)},
                            true,
                        )}
                    </span>
                </BlockList.Item>
                <BlockList.Item icon={<Icon label="" size="xsmall" glyph="coupon" />}>
                    <span className="ud-text-sm">{gettext('Cancel anytime')}</span>
                </BlockList.Item>
                <BlockList.Item icon={<Icon label="" size="xsmall" glyph="info-outline" />}>
                    <a
                        className={styles['learn-more-link']}
                        data-purpose="non-catalog-pp-learn-more-link"
                        href={learnMoreUrl}
                        target={learnMoreTarget}
                        onClick={handleLearnMoreClick}
                    >
                        {learnMoreText}
                    </a>
                </BlockList.Item>
            </BlockList>
            <SubscriptionCTA
                className={styles['cta-container']}
                uiRegion={UI_REGION}
                showSubtitle={false}
                data-purpose="non-catalog-pp-cta"
                offerCtaStyle="primary"
                onCTAClick={handleCtaClick}
                courseId={parseInt(course.id, 10)}
                courseTrackingId={course.frontendTrackingId}
                includeTrialDaysOnCta={false}
                plan={nonCatalogPersonalPlan}
                directCheckout={nonCatalogSubscriptionCTAConfiguration?.directCheckout ?? true}
                purchaseCtaText={
                    nonCatalogSubscriptionCTAConfiguration?.purchaseCtaText ??
                    gettext('Start subscription')
                }
                noticeData={{cta_1_url: ctaUrl}}
            />
        </CollapsiblePurchaseSection>
    );
};
